import { z } from "zod";
import { SymbolRefSchema } from "./environment-view.js";
/**
 * THE NARRATIVE-TO-SCENE VOCABULARY (ADR 0014). Everything a collaboratively
 * written scene can do to the architectural section, as typed data. Four
 * schemas, one pipeline:
 *
 *   StoryEventCandidate — what an INTERPRETER (a model, or the deterministic
 *                         fallback) says the prose did. Mentions are free
 *                         text ("Priya", "the interview room", "the folder");
 *                         the compiler resolves them or rejects them. A
 *                         candidate has no authority: it is a claim about
 *                         the text, never a state mutation (Law 1, Law 4).
 *   StoryEvent          — a candidate the compiler ADMITTED: every mention
 *                         resolved to an id the environment definition knows,
 *                         continuity checked, provenance attached. The
 *                         append-only log the world state folds over.
 *   WorldState          — the canonical PHYSICAL state of the stage: who is
 *                         present, where, in which pose, carrying what; every
 *                         fixture's state; which rooms are inked in. A pure
 *                         fold over admitted events (ADR 0002's shape).
 *   ScenePlan           — the resolved, transient motion instructions the
 *                         client plays between two EnvironmentViews: travel
 *                         along named places, a pose change, an appearance, a
 *                         vanishing. The client reads a path; it never decides
 *                         one.
 *
 * THIS MODULE DELIBERATELY IMPORTS NOTHING MECHANICAL (lint-enforced beside
 * environment-view.ts): no Band, Fidelity, Disposition, clock or lead may
 * appear here. A story event says "Renata left the room", never why the
 * engine let it happen. The vocabulary is physical by construction so that a
 * model choosing from it cannot smuggle a mechanic into the drawing.
 */
const TokenSchema = z.string().regex(/^[a-z][a-z0-9-]*$/, "must be a lowercase token");
/**
 * The frozen event vocabulary (the research report's phase 1: "freeze 15–25
 * kinds"). Adding a kind is a contract change reviewed as one; the
 * interpreter prompt lists exactly these and the compiler handles exactly
 * these. Every kind names something the section can SHOW.
 */
export const STORY_EVENT_KINDS = [
    /** A person who was not on the stage is now on it (they appear at an entry and go to a place). */
    "actor_arrived",
    /** A person leaves the stage by an exit and is no longer present. */
    "actor_departed",
    /** A person goes to a place (a room, or a named spot in one). */
    "actor_moved",
    /** A person sits (at a seat, the nearest seat, or the seat named). */
    "actor_sat",
    /** A person stands up. */
    "actor_stood",
    /** A person turns to face left or right. */
    "actor_turned",
    /** A person picks up a prop and carries it. */
    "actor_took",
    /** A person sets a carried prop down on a surface. */
    "actor_put_down",
    /** A person hands a prop to another person. */
    "actor_gave",
    /** A person turns their attention to another person (faces them). */
    "actor_observed",
    /** Two people in the same room face each other. */
    "conversation_began",
    /** They stop. */
    "conversation_ended",
    /** A fixture takes a new physical state (a sign lit, a door closed, blinds down). */
    "fixture_changed",
    /** A room is inked in: it has been seen. */
    "area_discovered",
    /** Someone breaks in on a room from its threshold — an arrival at the door, at pace. */
    "interruption",
];
export const StoryEventKindSchema = z.enum(STORY_EVENT_KINDS);
/** How fast a movement plays. A physical quality of the walk, not a judgement of it. */
export const PaceSchema = z.enum(["slow", "normal", "fast"]);
/** Who wrote the line the event was read from. Storylets are authored reactions, not lines. */
export const NarrationSpeakerSchema = z.enum(["learner", "narrator"]);
/** One line of the collaborative scene, as the interpreter reads it. */
export const NarrationLineSchema = z.strictObject({
    speaker: NarrationSpeakerSchema,
    text: z.string().min(1),
});
/**
 * A place on the stage: a room, optionally a named spot in it. When `slot`
 * is null the compiler chooses the nearest free spot of the right kind —
 * the prose said "the war room", not which chair.
 */
export const PlaceRefSchema = z.strictObject({
    room: TokenSchema,
    slot: TokenSchema.nullable(),
});
/** A place with the spot decided — what routes and plans are made of. */
export const StationRefSchema = z.strictObject({
    room: TokenSchema,
    slot: TokenSchema,
});
/**
 * THE INTERPRETER'S OUTPUT SHAPE — a model's, or the fallback's. Mentions,
 * not ids: "Priya", "the interview room", "her tablet". Resolution against
 * the environment definition is the compiler's job and its refusal is the
 * compiler's right (an unknown person is a rejection with a repair hint,
 * never a new person on the stage). `evidence` is the verbatim span of the
 * prose the claim rests on — every admitted event carries its sentence into
 * the audit tape.
 */
export const StoryEventCandidateSchema = z.strictObject({
    kind: StoryEventKindSchema,
    /** Who did it, as the text names them. Required for every actor_* kind; null for fixture/area kinds. */
    actor: z.string().min(1).nullable(),
    /** The other person, for gave / observed / conversation kinds. */
    counterpart: z.string().min(1).nullable(),
    /** Where, as the text names it (a room, a spot, "the door"). */
    place: z.string().min(1).nullable(),
    /** The thing carried or handed over, as the text names it. */
    prop: z.string().min(1).nullable(),
    /** The fixture, as the text names it, and the state it took ("closed", "lit"). */
    fixture: z.string().min(1).nullable(),
    state: z.string().min(1).nullable(),
    pace: PaceSchema.nullable(),
    /** The verbatim sentence (or clause) that says so. */
    evidence: z.string().min(1),
    speaker: NarrationSpeakerSchema,
});
/** What a scene interpreter returns, whole. */
export const SceneInterpretationSchema = z.strictObject({
    events: z.array(StoryEventCandidateSchema).max(24),
});
/**
 * Where an admitted event came from. A storylet is an AUTHORED reaction
 * (see StoryletSchema); its citation is the authored source the event
 * ultimately renders, in the ADR 0009 sense of "every binding cites".
 */
export const StoryEventSourceSchema = z.enum(["learner", "narrator", "storylet"]);
/**
 * AN ADMITTED EVENT. Every reference is an id the definition knows: an
 * actor id (the last segment of a cast symbol, or "player"), a room id, a
 * slot id, a symbol ref, a fixture id. `repair` records a deterministic
 * correction the compiler made on the way in (an "arrival" of someone
 * already present became a move) so the audit tape shows the candidate and
 * what was done with it.
 */
export const StoryEventSchema = z.strictObject({
    eventId: z.string().min(1),
    turn: z.number().int().min(0),
    kind: StoryEventKindSchema,
    source: StoryEventSourceSchema,
    /** The storylet that authored this event, when source is "storylet". */
    storyletId: z.string().min(1).nullable(),
    actor: TokenSchema.nullable(),
    counterpart: TokenSchema.nullable(),
    place: PlaceRefSchema.nullable(),
    prop: SymbolRefSchema.nullable(),
    fixture: TokenSchema.nullable(),
    state: TokenSchema.nullable(),
    /** Facing, for actor_turned only. */
    facing: z.enum(["left", "right"]).nullable(),
    pace: PaceSchema,
    evidence: z.string().min(1),
    repair: z.string().nullable(),
});
/** Why a candidate was not admitted. Closed so the inspector can group them and the prompt can be tuned against them. */
export const RejectionReasonSchema = z.enum([
    "free-turn",
    "unknown-actor",
    "unknown-counterpart",
    "unknown-place",
    "unknown-prop",
    "unknown-fixture",
    "missing-actor",
    "not-present",
    "not-colocated",
    "no-route",
    "no-seat",
    "no-free-spot",
    "no-exit",
    "no-entry",
    "not-carrying",
    "nothing-changed",
]);
export const RejectedCandidateSchema = z.strictObject({
    candidate: StoryEventCandidateSchema,
    reason: RejectionReasonSchema,
    /** A human-readable hint for the repair path ("no person called 'Dana' is on this stage; known: hugh, grant, renata"). */
    hint: z.string().min(1),
});
/**
 * Access an actor has through the stage's portals. Ordered: a "restricted"
 * pass opens everything. Physical (a badge), not mechanical.
 */
export const ClearanceSchema = z.enum(["public", "staff", "restricted"]);
export const WorldActorSchema = z.strictObject({
    symbolRef: SymbolRefSchema,
    present: z.boolean(),
    room: TokenSchema,
    slot: TokenSchema,
    pose: TokenSchema,
    facing: z.enum(["left", "right"]),
    carrying: z.array(SymbolRefSchema),
    /** The person they are turned toward, if any. */
    attention: TokenSchema.nullable(),
    clearance: ClearanceSchema,
});
export const WorldObjectSchema = z.strictObject({
    symbolRef: SymbolRefSchema,
    room: TokenSchema,
    slot: TokenSchema,
});
/**
 * CANONICAL PHYSICAL STATE of the stage. The research report separates
 * canonical facts, physical state, narrative obligations, and presentation
 * state; in P&C the first (facts) and third (clocks, leads, promises) are
 * already the engine's, and presentation state is the client's — so this
 * type is the second alone. Nothing here is a fact about the case.
 */
export const WorldStateSchema = z.strictObject({
    envId: z.string().min(1),
    turn: z.number().int().min(0),
    actors: z.record(TokenSchema, WorldActorSchema),
    fixtures: z.record(TokenSchema, TokenSchema),
    objects: z.array(WorldObjectSchema),
    discovered: z.array(TokenSchema),
    /** Ids of once-only storylets that have fired. */
    firedStorylets: z.array(z.string().min(1)),
    /** Pairs currently facing each other. */
    conversations: z.array(z.tuple([TokenSchema, TokenSchema])),
});
/**
 * THE SCENE PLAN: transient, resolved, physical. The client plays these
 * between the previous view and this one and then forgets them; every
 * step names the event it serves, so a step with no event is impossible
 * by construction.
 */
const StepBase = { eventId: z.string().min(1) };
export const SceneStepSchema = z.discriminatedUnion("kind", [
    z.strictObject({ ...StepBase, kind: z.literal("travel"), actor: TokenSchema, path: z.array(StationRefSchema).min(2), pose: TokenSchema, durationMs: z.number().int().min(0) }),
    z.strictObject({ ...StepBase, kind: z.literal("pose"), actor: TokenSchema, pose: TokenSchema, durationMs: z.number().int().min(0) }),
    z.strictObject({ ...StepBase, kind: z.literal("face"), actor: TokenSchema, facing: z.enum(["left", "right"]) }),
    z.strictObject({ ...StepBase, kind: z.literal("appear"), actor: TokenSchema, at: StationRefSchema }),
    z.strictObject({ ...StepBase, kind: z.literal("vanish"), actor: TokenSchema, at: StationRefSchema }),
    z.strictObject({ ...StepBase, kind: z.literal("carry"), actor: TokenSchema, prop: SymbolRefSchema, carrying: z.boolean() }),
    z.strictObject({ ...StepBase, kind: z.literal("fixture"), fixture: TokenSchema, state: TokenSchema }),
    z.strictObject({ ...StepBase, kind: z.literal("ink"), room: TokenSchema }),
]);
export const ScenePlanSchema = z.strictObject({
    turn: z.number().int().min(0),
    steps: z.array(SceneStepSchema),
});
/**
 * STORYLETS (report: "storylets plus HTN"). An authored reaction: when its
 * conditions hold over the world state and this turn's admitted events, its
 * directives are admitted as events with source "storylet". Conditions are
 * TYPED, never expression strings, so the director is a lookup and not an
 * evaluator. Each storylet CITES the authored source it stages (ADR 0009's
 * authored-tells rule): an NPC's disposition tell, a clock tell, a cast
 * station in the environment definition. A storylet with no citation is a
 * validation error, not a defect found later.
 */
export const StoryletConditionSchema = z.discriminatedUnion("kind", [
    /** An event of this kind (by this actor, if named) was admitted this turn. */
    z.strictObject({ kind: z.literal("event"), eventKind: StoryEventKindSchema, actor: TokenSchema.nullable() }),
    z.strictObject({ kind: z.literal("actor-present"), actor: TokenSchema, present: z.boolean() }),
    z.strictObject({ kind: z.literal("actor-in-room"), actor: TokenSchema, room: TokenSchema }),
    z.strictObject({ kind: z.literal("actor-pose"), actor: TokenSchema, pose: TokenSchema }),
    z.strictObject({ kind: z.literal("fixture-state"), fixture: TokenSchema, state: TokenSchema }),
    z.strictObject({ kind: z.literal("room-discovered"), room: TokenSchema }),
]);
/** A directive is an event the storylet authors — ids already resolved, provenance added by the director. */
export const StoryletDirectiveSchema = z.strictObject({
    kind: StoryEventKindSchema,
    actor: TokenSchema.nullable(),
    counterpart: TokenSchema.nullable().default(null),
    place: PlaceRefSchema.nullable().default(null),
    prop: SymbolRefSchema.nullable().default(null),
    fixture: TokenSchema.nullable().default(null),
    state: TokenSchema.nullable().default(null),
    facing: z.enum(["left", "right"]).nullable().default(null),
    pace: PaceSchema.default("normal"),
    /** The authored prose the section's accessible description carries for this reaction. */
    evidence: z.string().min(1),
});
export const StoryletCitationSchema = z.strictObject({
    kind: z.enum(["npc-disposition", "clock-threshold", "consequence-prose", "environment-scene", "environment-definition", "system-grammar"]),
    ref: z.string().min(1),
    text: z.string().min(1),
});
export const StoryletSchema = z.strictObject({
    id: z.string().regex(/^beat\.[a-z0-9-]+$/, "storylet ids look like beat.client-breaks-off"),
    label: z.string().min(1),
    /** Higher fires first within a turn. */
    priority: z.number().int().min(0).max(100),
    once: z.boolean().default(true),
    when: z.array(StoryletConditionSchema).min(1),
    scene: z.array(StoryletDirectiveSchema).min(1),
    cites: StoryletCitationSchema,
});
export const StoryletLibrarySchema = z.strictObject({
    envId: z.string().regex(/^env\.[a-z0-9.-]+$/),
    storylets: z.array(StoryletSchema),
});
//# sourceMappingURL=story-event.js.map